import * as React from 'react';
import {
    Grid,
    FormControl,
    Select,
    MenuItem,
    TextField,
    Button,
    IconButton,
    Stack,
    Typography,
    Box,
    Divider,
    Chip,
    InputLabel,
    Switch,
    FormControlLabel,
    Checkbox,
    Tooltip,
    Slider,
} from "@mui/material";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import CallEndIcon from "@mui/icons-material/CallEnd";
import PhoneForwardedIcon from "@mui/icons-material/PhoneForwarded";
import HttpIcon from "@mui/icons-material/Http";
import CodeIcon from "@mui/icons-material/Code";
import LabelIcon from "@mui/icons-material/Label";
import SummarizeIcon from "@mui/icons-material/Summarize";
import DialplanNode, {useDialplanNodeContext} from "./DialplanNode";
import {useDialplanEditorContext} from "./DialplanEditorContext";
import DialTargets from "./DialTargets";
import AddDialTarget from "./AddDialTarget";
import {useProfile} from "../../repository/ProfileRepository";

const AGENT_TYPE_LABELS = {
    'BUILT_IN': "L'assistente AI gestisce la conversazione con il chiamante.",
    'BRING_YOUR_OWN': "Usa l'SDK per collegare il tuo assistente. Contatta il supporto per maggiori informazioni.",
};

const HTTP_METHODS = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH'];
const HANGUP_TOOL_DESCRIPTION = "Termina realmente la telefonata. Devi chiamare sempre questo tool subito dopo il saluto finale: dire soltanto 'arrivederci' non chiude la chiamata.";
const DEFAULT_POST_CALL_WEBHOOK = {
    url: '',
    method: 'POST',
    body: '{"startedAt":"%TIME%","aiDurationSeconds":%AI_DURATION_SECONDS%}',
    headers: {'Content-Type': 'application/json'},
};
const THINKING_LEVEL_MARKS = [
    {value: 1, label: 'Basso'},
    {value: 2, label: 'Normale'},
    {value: 3, label: 'Alto'},
];
const MAX_SYSTEM_PROMPT_LENGTH = 4096;
const DEFAULT_INTROSPECTION_PROMPT = "Analizza la conversazione appena conclusa. Usa gli strumenti configurati solo quando le relative condizioni sono soddisfatte. Il recap deve essere sintetico, fedele e utile a capire motivo ed esito della chiamata.";

function Agent() {
    const {node, setNodeChild} = useDialplanNodeContext();
    const {isEditMode} = useDialplanEditorContext();
    const {data: profile} = useProfile();

    const canSelectAgentType = (profile?.experimentalDialplanNodes ?? []).includes('AGENT');

    const agentType = node.agent?.type || 'BUILT_IN';
    const tools = node.agent?.tools || [];
    const recordAudio = node.recordAudio === true;
    const backgroundAudio = node.backgroundAudio || '';

    const setAgent = (agent) => setNodeChild("agent")(agent);
    const setRecordAudio = (value) => setNodeChild("recordAudio")(value);
    const setBackgroundAudio = (value) => setNodeChild("backgroundAudio")(value || undefined);
    const setPostCallWebhookEnabled = (enabled) => {
        const agent = {...node.agent};
        if (enabled) {
            agent.postCallWebhook = {
                ...DEFAULT_POST_CALL_WEBHOOK,
                headers: {...DEFAULT_POST_CALL_WEBHOOK.headers},
            };
        } else {
            delete agent.postCallWebhook;
        }
        setAgent(agent);
    };
    const setIntrospectionEnabled = (enabled) => setNodeChild("next")(enabled
        ? {...AgentIntrospection.default, tools: AgentIntrospection.default.tools.map(tool => ({...tool, node: {...tool.node}}))}
        : undefined);
    const setOpeningMessage = (value) => {
        const agent = {...node.agent};
        if (value) {
            agent.openingMessage = value;
        } else {
            delete agent.openingMessage;
        }
        setAgent(agent);
    };

    const setTools = (newTools) => {
        setAgent({...node.agent, tools: newTools});
    };

    const addTool = (toolType) => {
        const newTool = toolType === 'DIAL'
            ? {name: 'trasferisci_chiamata', description: 'Trasferisci la chiamata a un collaboratore', node: {type: 'DIAL', timeout: 40, targets: []}}
            : {name: 'invia_dati', description: 'Invia i dati raccolti al sistema', node: {type: 'WEBHOOK', url: '', method: 'POST', body: '{"payload": %PAYLOAD%, "caller": "%CALLER_NUMBER%"}', headers: {"Content-Type": "application/json"}}};
        setTools([...tools, newTool]);
    };

    const removeTool = (index) => {
        setTools(tools.filter((_, i) => i !== index));
    };

    const updateTool = (index, updatedTool) => {
        const newTools = [...tools];
        newTools[index] = updatedTool;
        setTools(newTools);
    };

    return (
        <Grid container direction="column" alignItems="center">
            <SmartToyIcon sx={{fontSize: 70}} color="primary"/>
            <Grid item xs={12} sx={{mb: 2, mt: 2}}>
                <Typography variant="body1" align="center">
                    La chiamata viene gestita dall'assistente AI.
                </Typography>
            </Grid>

            {isEditMode && canSelectAgentType && (
                <Grid item xs={12} sx={{mb: 2, width: '100%'}}>
                    <FormControl fullWidth>
                        <Typography variant="body1" sx={{mb: 1}}>Tipo di assistente</Typography>
                        <Select
                            value={agentType}
                            onChange={(e) => {
                                const type = e.target.value;
                                setAgent(type === 'BUILT_IN'
                                    ? {type, systemPrompt: '', voiceName: 'ANNA', thinkingLevel: 1, tools: [{name: 'hangup', description: HANGUP_TOOL_DESCRIPTION, node: {type: 'HANGUP'}}]}
                                    : {type});
                            }}
                        >
                            <MenuItem value="BUILT_IN">Built-in</MenuItem>
                            <MenuItem value="BRING_YOUR_OWN">Bring Your Own</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
            )}

            <Grid item xs={12} sx={{mb: 2}}>
                <Typography variant="body2" color="text.secondary" align="center">
                    {AGENT_TYPE_LABELS[agentType]}
                </Typography>
            </Grid>

            <Grid item xs={12} sx={{mb: 2, width: '100%'}}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={recordAudio}
                            onChange={(e) => setRecordAudio(e.target.checked)}
                            disabled={!isEditMode}
                        />
                    }
                    label="Registra audio della conversazione con l'assistente"
                />
                <Typography variant="caption" color="text.secondary" display="block">
                    Se attivo, la parte di chiamata gestita dall'assistente AI viene registrata e resa disponibile per 24 ore. Sei tenuto a informare il chiamante che la chiamata sarà registrata.
                </Typography>
            </Grid>

            <Grid item xs={12} sx={{mb: 2, width: '100%'}}>
                <FormControl fullWidth>
                    <InputLabel id="agent-background-audio-label">Audio di sottofondo</InputLabel>
                    <Select
                        labelId="agent-background-audio-label"
                        value={backgroundAudio}
                        label="Audio di sottofondo"
                        onChange={(e) => setBackgroundAudio(e.target.value)}
                        disabled={!isEditMode}
                    >
                        <MenuItem value="">Nessun audio</MenuItem>
                        <MenuItem value="office">Ufficio</MenuItem>
                        <MenuItem value="restaurant">Ristorante</MenuItem>
                    </Select>
                </FormControl>
            </Grid>

            {agentType === 'BUILT_IN' && (
                <Grid item xs={12} sx={{width: '100%'}}>
                    <OpeningMessageSection
                        value={node.agent?.openingMessage || ''}
                        voiceName={node.agent?.voiceName || 'ANNA'}
                        onMessageChange={setOpeningMessage}
                        onVoiceChange={(voiceName) => setAgent({...node.agent, voiceName})}
                        isEditMode={isEditMode}
                    />

                    <Divider sx={{my: 3}}/>

                    <ThinkingLevelSection
                        value={node.agent?.thinkingLevel || 1}
                        onChange={(thinkingLevel) => setAgent({...node.agent, thinkingLevel})}
                        isEditMode={isEditMode}
                    />

                    <Divider sx={{my: 3}}/>

                    <SystemPromptSection
                        value={node.agent?.systemPrompt || ''}
                        onChange={(val) => setAgent({...node.agent, systemPrompt: val})}
                        isEditMode={isEditMode}
                    />

                    <Divider sx={{my: 3}}/>

                    <ToolsSection
                        tools={tools}
                        isEditMode={isEditMode}
                        onAddTool={addTool}
                        onRemoveTool={removeTool}
                        onUpdateTool={updateTool}
                    />

                    <Divider sx={{my: 3}}/>

                    <PostCallWebhookSection
                        webhook={node.agent?.postCallWebhook}
                        isEditMode={isEditMode}
                        onEnabledChange={setPostCallWebhookEnabled}
                        onChange={(postCallWebhook) => setAgent({...node.agent, postCallWebhook})}
                    />

                    <Divider sx={{my: 3}}/>

                    <Box sx={{width: '100%'}}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={!!node.next}
                                    onChange={(event) => setIntrospectionEnabled(event.target.checked)}
                                    disabled={!isEditMode}
                                />
                            }
                            label="Analisi dopo la chiamata"
                        />
                        <Typography variant="body2" color="text.secondary" sx={{mb: node.next ? 2 : 0}}>
                            Quando la conversazione termina nell'assistente AI, un secondo agente può creare un recap, aggiungere etichette o chiamare webhook.
                        </Typography>
                        {node.next && <DialplanNode prop="next"/>}
                    </Box>
                </Grid>
            )}
        </Grid>
    );
}

export function AgentIntrospection() {
    const {node, setNodeChild} = useDialplanNodeContext();
    const {isEditMode} = useDialplanEditorContext();
    const tools = node.tools || [];

    const setTools = (newTools) => setNodeChild("tools")(newTools);
    const addTool = (toolType) => {
        const tool = toolType === 'SET_RECAP'
            ? {name: 'imposta_recap', description: 'Crea un recap sintetico della chiamata', node: {type: 'SET_RECAP'}}
            : toolType === 'ADD_LABEL'
                ? {name: 'aggiungi_etichetta', description: 'Aggiunge l’etichetta quando la conversazione soddisfa le condizioni indicate', node: {type: 'ADD_LABEL', label: ''}}
                : {name: 'invia_dati', description: 'Invia i dati ricavati dalla conversazione al sistema', node: {type: 'WEBHOOK', url: '', method: 'POST', body: '{"payload": %PAYLOAD%, "caller": "%CALLER_NUMBER%"}', headers: {"Content-Type": "application/json"}}};
        setTools([...tools, tool]);
    };
    const updateTool = (index, updatedTool) => {
        const updatedTools = [...tools];
        updatedTools[index] = updatedTool;
        setTools(updatedTools);
    };

    return (
        <Box sx={{p: 2, borderRadius: 2, border: '1px solid', borderColor: 'primary.light', bgcolor: 'rgba(25,118,210,0.03)'}}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{mb: 1}}>
                <SummarizeIcon color="primary"/>
                <Typography variant="subtitle1" fontWeight="medium">Agente di analisi</Typography>
                <Chip label="Dopo la chiamata" size="small" color="primary" variant="outlined"/>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                Questo agente non partecipa alla telefonata: analizza il testo della conversazione quando il nodo assistente termina.
            </Typography>

            <SystemPromptSection
                value={node.systemPrompt || ''}
                onChange={setNodeChild("systemPrompt")}
                isEditMode={isEditMode}
                title="Istruzioni per l'analisi"
                placeholder="Ad esempio: crea un recap con richiesta, dati raccolti ed esito; aggiungi l’etichetta urgente se il cliente richiede assistenza entro oggi..."
            />

            <Divider sx={{my: 3}}/>

            <IntrospectionToolsSection
                tools={tools}
                isEditMode={isEditMode}
                onAddTool={addTool}
                onRemoveTool={(index) => setTools(tools.filter((_, toolIndex) => toolIndex !== index))}
                onUpdateTool={updateTool}
            />
        </Box>
    );
}

AgentIntrospection.title = "Analisi dopo la chiamata";
AgentIntrospection.description = "Analizza la conversazione conclusa ed esegue azioni";
AgentIntrospection.canBeReplaced = false;
AgentIntrospection.default = {
    type: 'AGENT_INTROSPECTION',
    systemPrompt: DEFAULT_INTROSPECTION_PROMPT,
    tools: [
        {name: 'imposta_recap', description: 'Crea un recap sintetico della chiamata', node: {type: 'SET_RECAP'}},
    ],
};

function OpeningMessageSection({value, voiceName, onMessageChange, onVoiceChange, isEditMode}) {
    const hasInvalidLength = value.length > 0 && value.length < 5;

    return (
        <Box>
            <Typography variant="subtitle1" fontWeight="medium" sx={{mb: 1}}>
                Messaggio di apertura
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                Viene generato al salvataggio e riprodotto prima di collegare l'assistente alla chiamata.
            </Typography>
            {isEditMode ? (
                <Stack spacing={2}>
                    <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        maxRows={4}
                        label="Testo del messaggio"
                        value={value}
                        onChange={(event) => onMessageChange(event.target.value)}
                        placeholder="Ad esempio: Buongiorno, sono l'assistente virtuale. Come posso aiutarla?"
                        inputProps={{maxLength: 1024}}
                        error={hasInvalidLength}
                        helperText={hasInvalidLength
                            ? 'Il messaggio deve contenere almeno 5 caratteri'
                            : `${value.length}/1024 caratteri · lascia vuoto per il comportamento automatico`}
                    />
                    <FormControl fullWidth>
                        <InputLabel id="agent-voice-label">Voce assistente</InputLabel>
                        <Select
                            labelId="agent-voice-label"
                            value={voiceName}
                            label="Voce assistente"
                            onChange={(event) => onVoiceChange(event.target.value)}
                        >
                            <MenuItem value="ANNA">👩 Anna</MenuItem>
                            <MenuItem value="GIOVANNI">👨 Giovanni</MenuItem>
                        </Select>
                    </FormControl>
                </Stack>
            ) : (
                <Box sx={{p: 2, borderRadius: 1, border: '1px dashed rgba(0,0,0,0.12)', bgcolor: 'rgba(0,0,0,0.02)'}}>
                    <Typography variant="body2" sx={{whiteSpace: 'pre-wrap'}}>
                        {value || 'Nessun messaggio di apertura configurato'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Voce: {voiceName === 'GIOVANNI' ? 'Giovanni' : 'Anna'}
                    </Typography>
                </Box>
            )}
        </Box>
    );
}

function ThinkingLevelSection({value, onChange, isEditMode}) {
    return (
        <Box>
            <Typography variant="subtitle1" fontWeight="medium" sx={{mb: 1}}>
                Livello di ragionamento
            </Typography>
            <Typography variant="body2" color="text.secondary">
                Un livello più alto può migliorare le risposte complesse, ma rende l'assistente più lento.
            </Typography>
            <Box sx={{px: 2, pt: 2}}>
                <Slider
                    value={value}
                    onChange={(_, thinkingLevel) => onChange(thinkingLevel)}
                    min={1}
                    max={3}
                    step={null}
                    marks={THINKING_LEVEL_MARKS}
                    disabled={!isEditMode}
                    aria-label="Livello di ragionamento"
                />
            </Box>
        </Box>
    );
}

function PostCallWebhookSection({webhook, isEditMode, onEnabledChange, onChange}) {
    return (
        <Box sx={{width: '100%'}}>
            <FormControlLabel
                control={
                    <Switch
                        checked={!!webhook}
                        onChange={(event) => onEnabledChange(event.target.checked)}
                        disabled={!isEditMode}
                    />
                }
                label="Webhook di fine chiamata"
            />
            <Typography variant="body2" color="text.secondary" sx={{mb: webhook ? 2 : 0}}>
                Il webhook parte solo quando termina l'intera chiamata, anche dopo un trasferimento a operatore. La durata include soltanto le sessioni AI.
            </Typography>
            {webhook && (isEditMode
                ? (
                    <WebhookRequestConfig
                        node={webhook}
                        onChange={onChange}
                        fieldsEnabled={false}
                        placeholderText="Placeholder: %CALLER_NUMBER%, %CALLEE_NUMBER%, %CALL_UUID%, %TIME%, %AI_DURATION_SECONDS% (numero)"
                    />
                )
                : (
                    <Typography variant="body2">
                        {webhook.method} {webhook.url}
                    </Typography>
                ))}
        </Box>
    );
}

function SystemPromptSection({value, onChange, isEditMode, title = 'Istruzioni agente', placeholder = "Descrivi il comportamento dell'agente, ad esempio: Sei un assistente telefonico per una pizzeria..."}) {
    const isTooLong = value.length > MAX_SYSTEM_PROMPT_LENGTH;

    return (
        <Box>
            <Typography variant="subtitle1" fontWeight="medium" sx={{mb: 1}}>
                {title}
            </Typography>
            {isEditMode ? (
                <TextField
                    fullWidth
                    multiline
                    minRows={12}
                    maxRows={24}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    inputProps={{maxLength: MAX_SYSTEM_PROMPT_LENGTH, spellCheck: true}}
                    error={isTooLong}
                    helperText={`${value.length}/${MAX_SYSTEM_PROMPT_LENGTH} caratteri · Placeholder disponibili: %CALLER_NUMBER%, %CALLEE_NUMBER%, %CALL_UUID%, %TIME%`}
                    sx={{
                        '& textarea': {
                            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                            lineHeight: 1.6,
                        },
                    }}
                />
            ) : (
                <Box sx={{p: 2, borderRadius: 1, border: '1px dashed rgba(0,0,0,0.12)', bgcolor: 'rgba(0,0,0,0.02)'}}>
                    <SimpleMarkdown value={value}/>
                </Box>
            )}
        </Box>
    );
}

function SimpleMarkdown({value}) {
    if (!value) {
        return <Typography variant="body2" color="text.secondary">Nessuna istruzione configurata</Typography>;
    }

    const lines = value.split('\n');
    const blocks = [];
    let index = 0;

    while (index < lines.length) {
        const line = lines[index];
        const heading = line.match(/^(#{1,3})\s+(.+)$/);
        if (heading) {
            const variant = heading[1].length === 1 ? 'h5' : heading[1].length === 2 ? 'h6' : 'subtitle1';
            blocks.push(
                <Typography key={`heading-${index}`} variant={variant} fontWeight="bold" sx={{mt: blocks.length ? 2 : 0, mb: 1}}>
                    {heading[2]}
                </Typography>
            );
            index += 1;
            continue;
        }

        if (/^\s*[*-]\s+/.test(line)) {
            const items = [];
            const listStart = index;
            while (index < lines.length && /^\s*[*-]\s+/.test(lines[index])) {
                items.push(lines[index].replace(/^\s*[*-]\s+/, ''));
                index += 1;
            }
            blocks.push(
                <Box component="ul" key={`list-${listStart}`} sx={{my: 1, pl: 3}}>
                    {items.map((item, itemIndex) => (
                        <Typography component="li" variant="body2" key={`${listStart}-${itemIndex}`} sx={{mb: 0.5}}>
                            {item}
                        </Typography>
                    ))}
                </Box>
            );
            continue;
        }

        if (line.trim()) {
            const paragraph = [];
            const paragraphStart = index;
            while (
                index < lines.length
                && lines[index].trim()
                && !/^(#{1,3})\s+/.test(lines[index])
                && !/^\s*[*-]\s+/.test(lines[index])
            ) {
                paragraph.push(lines[index]);
                index += 1;
            }
            blocks.push(
                <Typography key={`paragraph-${paragraphStart}`} variant="body2" sx={{whiteSpace: 'pre-wrap', mb: 1}}>
                    {paragraph.join('\n')}
                </Typography>
            );
            continue;
        }

        index += 1;
    }

    return blocks;
}

function IntrospectionToolsSection({tools, isEditMode, onAddTool, onRemoveTool, onUpdateTool}) {
    const [addMenuOpen, setAddMenuOpen] = React.useState(false);
    const duplicateNames = tools.filter((tool, index) => tools.findIndex(candidate => candidate.name === tool.name) !== index);

    return (
        <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{mb: 2}}>
                <Typography variant="subtitle1" fontWeight="medium">Strumenti di analisi</Typography>
                {isEditMode && (
                    <Box sx={{position: 'relative'}}>
                        <Button startIcon={<AddIcon/>} size="small" variant="outlined" onClick={() => setAddMenuOpen(!addMenuOpen)}>
                            Aggiungi strumento
                        </Button>
                        {addMenuOpen && (
                            <Box sx={{position: 'absolute', right: 0, top: '100%', mt: 0.5, zIndex: 10, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 3, overflow: 'hidden', minWidth: 220}}>
                                <MenuItem disabled={tools.some(tool => tool.node.type === 'SET_RECAP')} onClick={() => { onAddTool('SET_RECAP'); setAddMenuOpen(false); }}>
                                    <SummarizeIcon sx={{mr: 1, fontSize: 20}}/> Imposta recap
                                </MenuItem>
                                <MenuItem onClick={() => { onAddTool('ADD_LABEL'); setAddMenuOpen(false); }}>
                                    <LabelIcon sx={{mr: 1, fontSize: 20}}/> Aggiungi etichetta
                                </MenuItem>
                                <MenuItem onClick={() => { onAddTool('WEBHOOK'); setAddMenuOpen(false); }}>
                                    <HttpIcon sx={{mr: 1, fontSize: 20}}/> Richiesta HTTP
                                </MenuItem>
                            </Box>
                        )}
                    </Box>
                )}
            </Stack>

            {duplicateNames.length > 0 && (
                <Typography variant="caption" color="error" sx={{display: 'block', mb: 1}}>
                    I nomi degli strumenti devono essere univoci.
                </Typography>
            )}

            <Stack spacing={2}>
                {tools.map((tool, index) => (
                    <IntrospectionToolCard
                        key={index}
                        tool={tool}
                        isEditMode={isEditMode}
                        onRemove={() => onRemoveTool(index)}
                        onUpdate={(updated) => onUpdateTool(index, updated)}
                    />
                ))}
            </Stack>
        </Box>
    );
}

function IntrospectionToolCard({tool, isEditMode, onRemove, onUpdate}) {
    const isNameInvalid = !/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(tool.name);
    const icon = tool.node.type === 'SET_RECAP'
        ? <SummarizeIcon sx={{fontSize: 20, color: 'primary.main'}}/>
        : tool.node.type === 'ADD_LABEL'
            ? <LabelIcon sx={{fontSize: 20, color: 'primary.main'}}/>
            : <HttpIcon sx={{fontSize: 20, color: 'primary.main'}}/>;
    const typeLabel = tool.node.type === 'SET_RECAP'
        ? 'Imposta recap'
        : tool.node.type === 'ADD_LABEL'
            ? 'Aggiungi etichetta'
            : 'Richiesta HTTP';

    return (
        <Box sx={{p: 2, borderRadius: 1, border: '1px solid rgba(0,0,0,0.12)', bgcolor: 'background.paper'}}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{mb: isEditMode ? 2 : 0}}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    {icon}
                    <Chip label={typeLabel} size="small" variant="outlined"/>
                    {!isEditMode && <Typography variant="body2" fontWeight="medium">{tool.name}</Typography>}
                </Stack>
                {isEditMode && <IconButton size="small" color="error" onClick={onRemove}><DeleteIcon fontSize="small"/></IconButton>}
            </Stack>

            {isEditMode ? (
                <Stack spacing={2}>
                    <Stack direction="row" spacing={2}>
                        <TextField
                            fullWidth size="small" label="Nome" value={tool.name}
                            onChange={(event) => onUpdate({...tool, name: event.target.value})}
                            inputProps={{maxLength: 64}}
                            error={isNameInvalid}
                            helperText={isNameInvalid ? 'Usa solo lettere, numeri e underscore; il nome non può iniziare con un numero' : undefined}
                        />
                        <TextField
                            fullWidth size="small" label="Descrizione" value={tool.description}
                            onChange={(event) => onUpdate({...tool, description: event.target.value})}
                        />
                    </Stack>
                    {tool.node.type === 'ADD_LABEL' && (
                        <TextField
                            fullWidth size="small" label="Etichetta" value={tool.node.label || ''}
                            inputProps={{maxLength: 20}}
                            onChange={(event) => onUpdate({...tool, node: {...tool.node, label: event.target.value}})}
                            error={!tool.node.label?.trim()}
                            helperText={!tool.node.label?.trim() ? 'Obbligatoria' : `${tool.node.label.length}/20 caratteri`}
                        />
                    )}
                    {tool.node.type === 'WEBHOOK' && (
                        <WebhookRequestConfig node={tool.node} onChange={(newNode) => onUpdate({...tool, node: newNode})}/>
                    )}
                </Stack>
            ) : (
                <>
                    {tool.description && <Typography variant="body2" color="text.secondary" sx={{mt: 0.5}}>{tool.description}</Typography>}
                    {tool.node.type === 'ADD_LABEL' && <Typography variant="caption" color="text.secondary">Etichetta: {tool.node.label}</Typography>}
                </>
            )}
        </Box>
    );
}

function ToolsSection({tools, isEditMode, onAddTool, onRemoveTool, onUpdateTool}) {
    const [addMenuOpen, setAddMenuOpen] = React.useState(false);

    return (
        <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{mb: 2}}>
                <Typography variant="subtitle1" fontWeight="medium">
                    Strumenti
                </Typography>
                {isEditMode && (
                    <Box sx={{position: 'relative'}}>
                        <Button
                            startIcon={<AddIcon/>}
                            size="small"
                            variant="outlined"
                            onClick={() => setAddMenuOpen(!addMenuOpen)}
                        >
                            Aggiungi strumento
                        </Button>
                        {addMenuOpen && (
                            <Box sx={{
                                position: 'absolute', right: 0, top: '100%', mt: 0.5, zIndex: 10,
                                bgcolor: 'background.paper', borderRadius: 1,
                                boxShadow: 3, overflow: 'hidden',
                            }}>
                                <MenuItem onClick={() => { onAddTool('DIAL'); setAddMenuOpen(false); }}>
                                    <PhoneForwardedIcon sx={{mr: 1, fontSize: 20}}/> Trasferisci chiamata
                                </MenuItem>
                                <MenuItem onClick={() => { onAddTool('WEBHOOK'); setAddMenuOpen(false); }}>
                                    <HttpIcon sx={{mr: 1, fontSize: 20}}/> Richiesta HTTP
                                </MenuItem>
                            </Box>
                        )}
                    </Box>
                )}
            </Stack>

            <Stack spacing={2}>
                {tools.map((tool, index) => (
                    <ToolCard
                        key={index}
                        tool={tool}
                        index={index}
                        isEditMode={isEditMode}
                        isRemovable={tool.node.type !== 'HANGUP'}
                        onRemove={() => onRemoveTool(index)}
                        onUpdate={(updated) => onUpdateTool(index, updated)}
                    />
                ))}
            </Stack>
        </Box>
    );
}

function ToolCard({tool, isEditMode, isRemovable, onRemove, onUpdate}) {
    const nodeType = tool.node.type;
    const description = nodeType === 'HANGUP' ? HANGUP_TOOL_DESCRIPTION : tool.description;

    const icon = nodeType === 'HANGUP'
        ? <CallEndIcon sx={{fontSize: 20, color: 'error.main'}}/>
        : nodeType === 'DIAL'
            ? <PhoneForwardedIcon sx={{fontSize: 20, color: 'primary.main'}}/>
            : <HttpIcon sx={{fontSize: 20, color: 'primary.main'}}/>;

    const typeLabel = nodeType === 'HANGUP' ? 'Termina chiamata'
        : nodeType === 'DIAL' ? 'Trasferisci chiamata'
            : 'Richiesta HTTP';

    return (
        <Box sx={{p: 2, borderRadius: 1, border: '1px solid rgba(0,0,0,0.12)', bgcolor: 'background.paper'}}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{mb: isEditMode ? 2 : 0}}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    {icon}
                    <Chip label={typeLabel} size="small" variant="outlined"/>
                    {!isEditMode && <Typography variant="body2" fontWeight="medium">{tool.name}</Typography>}
                </Stack>
                {isEditMode && isRemovable && (
                    <IconButton size="small" color="error" onClick={onRemove}>
                        <DeleteIcon fontSize="small"/>
                    </IconButton>
                )}
            </Stack>

            {isEditMode && (
                <Stack spacing={2}>
                    <Stack direction="row" spacing={2}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Nome"
                            value={tool.name}
                            onChange={(e) => onUpdate({...tool, name: e.target.value})}
                            disabled={nodeType === 'HANGUP'}
                            error={nodeType !== 'HANGUP' && !tool.name.trim()}
                            helperText={nodeType !== 'HANGUP' && !tool.name.trim() ? 'Obbligatorio' : undefined}
                        />
                        <TextField
                            fullWidth
                            size="small"
                            label="Descrizione"
                            value={description}
                            onChange={(e) => onUpdate({...tool, description: e.target.value})}
                            disabled={nodeType === 'HANGUP'}
                        />
                    </Stack>

                    {nodeType === 'DIAL' && (
                        <ToolDialConfig
                            node={tool.node}
                            onChange={(newNode) => onUpdate({...tool, node: newNode})}
                        />
                    )}

                    {nodeType === 'WEBHOOK' && (
                        <WebhookRequestConfig
                            node={tool.node}
                            onChange={(newNode) => onUpdate({...tool, node: newNode})}
                        />
                    )}
                </Stack>
            )}

            {!isEditMode && description && (
                <Typography variant="body2" color="text.secondary" sx={{mt: 0.5}}>
                    {description}
                </Typography>
            )}
        </Box>
    );
}

function ToolDialConfig({node, onChange}) {
    const {dialplan} = useDialplanEditorContext();

    const setTargets = (targets) => onChange({...node, targets});

    return (
        <Box>
            <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
                Configura i collaboratori a cui trasferire la chiamata.
            </Typography>

            <Box sx={{minHeight: 60, mb: 1}}>
                <DialTargets targets={node.targets || []} setTargets={setTargets} isEditMode={true}/>
            </Box>

            {dialplan?.allowedDialTargets?.length > 0 && (
                <AddDialTarget targets={node.targets || []} setTargets={setTargets}/>
            )}

            <FormControl fullWidth sx={{mt: 2}} size="small">
                <InputLabel>Tempo massimo di attesa</InputLabel>
                <Select
                    value={node.timeout || 60}
                    label="Tempo massimo di attesa"
                    onChange={(e) => onChange({...node, timeout: e.target.value})}
                >
                    <MenuItem value={20}>20 secondi</MenuItem>
                    <MenuItem value={30}>30 secondi</MenuItem>
                    <MenuItem value={40}>40 secondi</MenuItem>
                    <MenuItem value={60}>1 minuto</MenuItem>
                </Select>
            </FormControl>
        </Box>
    );
}

function WebhookRequestConfig({
    node,
    onChange,
    fieldsEnabled = true,
    placeholderText = 'Placeholder: %CALLER_NUMBER%, %CALLEE_NUMBER%, %CALL_UUID%, %TIME%, %PAYLOAD% (oggetto con i campi raccolti)',
}) {
    const fields = node.fields || [];

    const [headers, setHeaders] = React.useState(() => {
        if (!node.headers) return [];
        return Object.entries(node.headers).map(([key, value]) => ({key, value}));
    });

    const hasIncompleteHeaders = headers.some(it => !it.key.trim() || !it.value.trim());
    const hasIncompleteFields = fields.some(it => !it.name.trim());

    const isJsonValid = React.useMemo(() => {
        if (!node.body || node.body.trim() === '') return true;
        const sanitized = node.body
            .replaceAll('"%PAYLOAD%"', '""')
            .replaceAll('%PAYLOAD%', '{}')
            .replaceAll('%AI_DURATION_SECONDS%', '1');
        try { JSON.parse(sanitized); return true; } catch { return false; }
    }, [node.body]);

    const urlValidation = React.useMemo(() => {
        if (!node.url || node.url.trim() === '') return {isValid: false, message: "L'URL e' obbligatorio"};
        try {
            const url = new URL(node.url);
            if (url.protocol !== 'http:' && url.protocol !== 'https:') return {isValid: false, message: "L'URL deve iniziare con http:// o https://"};
            return {isValid: true, message: ""};
        } catch { return {isValid: false, message: "L'URL non e' valido"}; }
    }, [node.url]);

    const updateHeaders = (newHeaders) => {
        setHeaders(newHeaders);
        const headersObj = newHeaders.reduce((obj, {key, value}) => {
            if (key.trim() && value.trim()) obj[key.trim()] = value.trim();
            return obj;
        }, {});
        onChange({...node, headers: headersObj});
    };

    const updateFields = (newFields) => {
        onChange({...node, fields: newFields});
    };

    return (
        <Stack spacing={2}>
            {fieldsEnabled && <Box>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{mb: 1}}>
                    <Typography variant="body2" fontWeight="medium">Campi da raccogliere</Typography>
                    <Button
                        startIcon={<AddIcon/>} size="small" variant="text"
                        onClick={() => updateFields([...fields, {name: '', description: '', required: true}])}
                        disabled={hasIncompleteFields}
                    >
                        Aggiungi campo
                    </Button>
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{mb: 1, display: 'block'}}>
                    L'agente raccogliera' questi dati dal chiamante e li inviera' nel body tramite il placeholder %PAYLOAD%.
                </Typography>
                {fields.map((field, index) => (
                    <Grid container spacing={1} key={index} sx={{mb: 1}} alignItems="center">
                        <Grid item xs={3}>
                            <TextField
                                fullWidth size="small" label="Nome campo"
                                value={field.name}
                                placeholder="nome_cliente"
                                onChange={(e) => {
                                    const f = [...fields]; f[index] = {...f[index], name: e.target.value};
                                    updateFields(f);
                                }}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth size="small" label="Descrizione"
                                value={field.description}
                                placeholder="Il nome completo del cliente"
                                onChange={(e) => {
                                    const f = [...fields]; f[index] = {...f[index], description: e.target.value};
                                    updateFields(f);
                                }}
                            />
                        </Grid>
                        <Grid item xs={2}>
                            <Tooltip title="Campo obbligatorio">
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            size="small"
                                            checked={field.required !== false}
                                            onChange={(e) => {
                                                const f = [...fields]; f[index] = {...f[index], required: e.target.checked};
                                                updateFields(f);
                                            }}
                                        />
                                    }
                                    label={<Typography variant="caption">Richiesto</Typography>}
                                />
                            </Tooltip>
                        </Grid>
                        <Grid item xs={1}>
                            <IconButton size="small" color="error" onClick={() => updateFields(fields.filter((_, i) => i !== index))}>
                                <DeleteIcon fontSize="small"/>
                            </IconButton>
                        </Grid>
                    </Grid>
                ))}
            </Box>}

            <Divider/>

            <Grid container spacing={2}>
                <Grid item xs={8}>
                    <TextField
                        fullWidth size="small" label="URL"
                        value={node.url || ''}
                        onChange={(e) => onChange({...node, url: e.target.value})}
                        placeholder="https://api.example.com/webhook"
                        error={!urlValidation.isValid}
                        helperText={!urlValidation.isValid ? urlValidation.message : undefined}
                    />
                </Grid>
                <Grid item xs={4}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Method</InputLabel>
                        <Select
                            value={node.method || 'POST'}
                            onChange={(e) => onChange({...node, method: e.target.value})}
                            label="Method"
                        >
                            {HTTP_METHODS.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>

            <Box>
                <Stack direction="row" alignItems="center" sx={{mb: 0.5}}>
                    <CodeIcon sx={{mr: 0.5, fontSize: 18, color: 'text.secondary'}}/>
                    <Typography variant="body2" fontWeight="medium">Body (JSON)</Typography>
                </Stack>
                <TextField
                    fullWidth size="small" multiline rows={3}
                    value={node.body || ''}
                    onChange={(e) => onChange({...node, body: e.target.value})}
                    placeholder={'{"data": %PAYLOAD%, "caller": "%CALLER_NUMBER%"}'}
                    sx={{fontFamily: 'monospace'}}
                    error={!isJsonValid && !!node.body?.trim()}
                />
                <Typography variant="caption" color="text.secondary" sx={{mt: 0.5, display: 'block'}}>
                    {placeholderText}
                </Typography>
            </Box>

            <Box>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{mb: 1}}>
                    <Typography variant="body2" fontWeight="medium">Headers</Typography>
                    <Button
                        startIcon={<AddIcon/>} size="small" variant="text"
                        onClick={() => updateHeaders([...headers, {key: '', value: ''}])}
                        disabled={hasIncompleteHeaders}
                    >
                        Aggiungi
                    </Button>
                </Stack>
                {headers.map((header, index) => (
                    <Grid container spacing={1} key={index} sx={{mb: 1}}>
                        <Grid item xs={5}>
                            <TextField
                                fullWidth size="small" label="Key"
                                value={header.key}
                                onChange={(e) => {
                                    const h = [...headers]; h[index] = {...h[index], key: e.target.value};
                                    updateHeaders(h);
                                }}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth size="small" label="Value"
                                value={header.value}
                                onChange={(e) => {
                                    const h = [...headers]; h[index] = {...h[index], value: e.target.value};
                                    updateHeaders(h);
                                }}
                            />
                        </Grid>
                        <Grid item xs={1}>
                            <IconButton size="small" color="error" onClick={() => {
                                updateHeaders(headers.filter((_, i) => i !== index));
                            }}>
                                <DeleteIcon fontSize="small"/>
                            </IconButton>
                        </Grid>
                    </Grid>
                ))}
            </Box>
        </Stack>
    );
}

Agent.title = "Assistente AI";
Agent.description = "Inoltra la chiamata all'assistente AI";
Agent.default = {
    type: 'AGENT',
    agent: {
        type: 'BUILT_IN',
        systemPrompt: '',
        voiceName: 'ANNA',
        thinkingLevel: 1,
        tools: [
            {name: 'hangup', description: HANGUP_TOOL_DESCRIPTION, node: {type: 'HANGUP'}},
        ],
    },
};

export const AgentBringYourOwnVariant = {
    title: Agent.title,
    description: Agent.description,
    isAdvanced: Agent.isAdvanced,
    canBeReplaced: Agent.canBeReplaced,
    default: {type: 'AGENT', agent: {type: 'BRING_YOUR_OWN'}},
};

export default Agent;
